import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DateRangeSelector from "../../../components/DateRangeSelector";
import { bloodBankDateSearch, todayIso } from "../../blood-donor/blood-bank-date";

type StockRow = {
  bloodGroup: string;
  count: number;
  PRC?: { count: number };
  FFP?: { count: number };
  PLT?: { count: number };
  Cryo?: { count: number };
  WB?: { count: number };
  Other?: { count: number };
};

type Stats = {
  donors: number;
  activeBags: number;
  issuedBags: number;
  expiredBags: number;
  disposedBags: number;
  screenings: number;
  crossMatches: number;
  patientSlips: number;
};

const emptyStats: Stats = {
  donors: 0,
  activeBags: 0,
  issuedBags: 0,
  expiredBags: 0,
  disposedBags: 0,
  screenings: 0,
  crossMatches: 0,
  patientSlips: 0,
};

const productColumns = ["PRC", "FFP", "PLT", "Cryo", "WB", "Other"] as const;

const groupColor: Record<string, string> = {
  "A+": "#c62828",
  "A-": "#ef6c00",
  "B+": "#1565c0",
  "B-": "#00838f",
  "AB+": "#6a1b9a",
  "AB-": "#8e24aa",
  "O+": "#2e7d32",
  "O-": "#558b2f",
};

const BloodBankDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>(emptyStats);
  const [stock, setStock] = useState<StockRow[]>([]);
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(todayIso);
  const [toDate, setToDate] = useState(todayIso);

  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };
    const base = process.env.REACT_APP_BLOOD_BANK;
    let cancelled = false;

    const fetchCount = async (url: string, params: Record<string, string | number> = {}) => {
      try {
        const response = await axios.get(`${base}${url}`, {
          headers,
          params: {
            pageNo: 1,
            count: 1,
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
            ...params,
          },
        });
        return Number(response.data?.totalCount) || 0;
      } catch {
        return 0;
      }
    };

    const load = async () => {
      setLoading(true);
      const [
        donors,
        activeBags,
        issuedBags,
        expiredBags,
        disposedBags,
        screenings,
        crossMatches,
        patientSlips,
        stockResponse,
      ] = await Promise.all([
        fetchCount("/donor/getAllDonorsForDashboard"),
        fetchCount("/bag-generation/findAllBags", { status: "ACTIVE" }),
        fetchCount("/bag-generation/findAllBags", { status: "ISSUED" }),
        fetchCount("/bag-generation/findAllBags", { status: "EXPIRED" }),
        fetchCount("/bag-generation/findAllBags", { status: "DISPOSED" }),
        fetchCount("/donor-screening/findAllScreenings"),
        fetchCount("/cross-match/findAllCrossMatches"),
        fetchCount("/patient-slip/findAllPatientSlips"),
        axios
          .get(`${base}/bag-generation/getAvailableStockState`, { headers })
          .catch(() => null),
      ]);

      if (cancelled) return;
      setStats({
        donors,
        activeBags,
        issuedBags,
        expiredBags,
        disposedBags,
        screenings,
        crossMatches,
        patientSlips,
      });
      const stockRows = stockResponse?.data?.data;
      setStock(Array.isArray(stockRows) ? stockRows : []);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate]);

  const openCard = (to: string, status?: string) => {
    const query = bloodBankDateSearch(dateRange, fromDate, toDate, status);
    navigate(query ? `${to}?${query}` : to);
  };

  const cards: {
    label: string;
    value: number;
    to: string;
    color: string;
    status?: string;
  }[] = [
    { label: "Donors", value: stats.donors, to: "/donor-history", color: "#1565c0" },
    { label: "Active Bags", value: stats.activeBags, to: "/donor-bag", status: "ACTIVE", color: "#2e7d32" },
    { label: "Issued Bags", value: stats.issuedBags, to: "/donor-bag", status: "ISSUED", color: "#ef6c00" },
    { label: "Expired Bags", value: stats.expiredBags, to: "/donor-bag", status: "EXPIRED", color: "#c62828" },
    { label: "Disposed Bags", value: stats.disposedBags, to: "/donor-bag", status: "DISPOSED", color: "#6d4c41" },
    { label: "Screenings", value: stats.screenings, to: "/donor-screening-reports", color: "#00838f" },
    { label: "Cross Matches", value: stats.crossMatches, to: "/blood-bank/cross-match-report", color: "#6a1b9a" },
    { label: "Patient Slips", value: stats.patientSlips, to: "/patient-slip", color: "#455a64" },
  ];

  const stockTotal = stock.reduce((sum, row) => sum + (Number(row.count) || 0), 0);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={1}
        sx={{ background: "#fff", p: 1.5, borderRadius: "8px", mb: 1 }}
      >
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
            Blood Bank
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            Counts follow the selected date range
          </Typography>
        </Box>
        <DateRangeSelector
          preset="blood"
          compact
          dateRange={dateRange}
          fromDate={fromDate}
          toDate={toDate}
          onDateRangeChange={setDateRange}
          onFromDateChange={setFromDate}
          onToDateChange={setToDate}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Grid container spacing={1}>
            {cards.map(card => (
              <Grid item xs={6} sm={4} md={3} key={card.label}>
                <Paper
                  onClick={() => openCard(card.to, card.status)}
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    cursor: "pointer",
                    borderTop: `3px solid ${card.color}`,
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                    {card.label}
                  </Typography>
                  <Typography sx={{ fontSize: "24px", fontWeight: 700, color: card.color }}>
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ mt: 1, p: 1.5, borderRadius: "8px" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: "15px", fontWeight: "bold" }}>
                Available Stock
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: "#1565c0", cursor: "pointer" }}
                onClick={() => navigate("/available-stock")}
              >
                {stockTotal} active components
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#1976d2" }}>
                  {["Blood Group", "Count", ...productColumns].map(header => (
                    <TableCell key={header} sx={{ color: "#fff", fontWeight: 700, fontSize: "12px" }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {stock.map(row => (
                  <TableRow key={row.bloodGroup} hover>
                    <TableCell sx={{ fontWeight: 700, color: groupColor[row.bloodGroup] || "#333" }}>
                      {row.bloodGroup}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{row.count || 0}</TableCell>
                    {productColumns.map(product => (
                      <TableCell key={product}>{row[product]?.count || 0}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {stock.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No stock data available.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default BloodBankDashboard;
